/** Distinct fills for pie slices when a category has no custom color (readable on light and dark backgrounds). */
const SLICE_COLORS = [
  "#5c9ded",
  "#7dffb2",
  "#ffb86b",
  "#ff7b9d",
  "#b388ff",
  "#5ee0e0",
  "#ffd166",
  "#ee6c8b",
  "#9ad0ff",
  "#c4f0a8",
  "#f4a582",
  "#d4b8ff",
];

export type PieSlice = {
  label: string;
  valueCents: number;
  /** Hex `#rrggbb` from category; omit or null to use palette by index. */
  color?: string | null;
};

type Props = {
  slices: PieSlice[];
  /** Accessible title, e.g. "Expense breakdown for April 2026" */
  title: string;
  /** Shown under the chart; defaults to expense wording. */
  caption?: string;
};

const DEFAULT_CAPTION = "Pie chart — expense share by category";

const CX = 50;
const CY = 50;
const R = 38;
/** Padding around 0–100 chart so callout labels fit inside the SVG. */
const VIEW_PAD = 12;
const VIEW_BOX = `${-VIEW_PAD} ${-VIEW_PAD} ${100 + 2 * VIEW_PAD} ${100 + 2 * VIEW_PAD}`;
/** Hide on-chart callouts for very thin wedges (legend still lists them). */
const LABEL_MIN_FRAC = 0.04;
/** Leader line: slice edge → short stub outward; label sits past the stub. */
const CALLOUT_INSET = 0.94;
/** Label center along wedge bisector, outside the pie. */
const CALLOUT_TEXT_R = R + 8.5;
/** Leader line ends just short of the label so it reads as one pointer. */
const CALLOUT_LINE_END_R = CALLOUT_TEXT_R - 2.3;
const LABEL_MAX_CHARS = 22;

function truncateLabel(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}

function isHexColor6(s: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(s.trim());
}

/** Use category color when valid hex; otherwise palette slot by index. */
export function sliceFillColor(color: string | null | undefined, paletteIndex: number): string {
  if (color && isHexColor6(color)) return color.trim();
  return SLICE_COLORS[paletteIndex % SLICE_COLORS.length];
}

/** @deprecated Prefer sliceFillColor(slice.color, index) for category-aware charts. */
export function sliceColor(index: number): string {
  return sliceFillColor(undefined, index);
}

type WedgeMeta = {
  d: string;
  fill: string;
  key: string;
  midAngle: number;
  frac: number;
  label: string;
};

/**
 * SVG pie chart. One category at 100% uses a circle (reliable full disc). Multiple slices use wedge paths.
 * Multi-slice: category name on a short radial leader from the slice; thin wedges stay legend-only.
 */
export function ExpensePieChart({ slices, title, caption = DEFAULT_CAPTION }: Props) {
  const total = slices.reduce((s, x) => s + x.valueCents, 0);
  if (total <= 0 || slices.length === 0) {
    return null;
  }

  const positive = slices.map((s, i) => ({ ...s, i })).filter((s) => s.valueCents > 0);
  if (positive.length === 0) return null;

  if (positive.length === 1) {
    const s0 = positive[0]!;
    const fill = sliceFillColor(s0.color, s0.i);
    const name = truncateLabel(s0.label, 24);
    return (
      <figure className="pie-chart-figure pie-chart-figure-single">
        <div className="pie-chart-wrap" role="img" aria-label={title}>
          <svg viewBox={VIEW_BOX} className="pie-chart-svg" aria-hidden overflow="visible">
            <title>{title}</title>
            <circle
              cx={CX}
              cy={CY}
              r={R}
              fill={fill}
              stroke="var(--border)"
              strokeWidth={1.25}
            />
            <text x={CX} y={CY} className="pie-chart-label pie-chart-label-stack pie-chart-label-single" textAnchor="middle">
              <tspan x={CX} dy="-1.6" className="pie-chart-label-name">
                {name}
              </tspan>
              <tspan x={CX} dy="5" className="pie-chart-label-pct">
                100%
              </tspan>
            </text>
          </svg>
        </div>
        <figcaption className="pie-chart-caption muted small">{caption}</figcaption>
      </figure>
    );
  }

  const gap = 0.03;
  const totalGaps = gap * positive.length;
  const sweepAvailable = 2 * Math.PI - totalGaps;
  let angle = -Math.PI / 2 + gap / 2;

  const wedges: WedgeMeta[] = [];

  for (let j = 0; j < positive.length; j++) {
    const slice = positive[j]!;
    const frac = slice.valueCents / total;
    const sweep = frac * sweepAvailable;
    const a0 = angle;
    const a1 = angle + sweep;
    const midAngle = a0 + sweep / 2;
    const x1 = CX + R * Math.cos(a0);
    const y1 = CY + R * Math.sin(a0);
    const x2 = CX + R * Math.cos(a1);
    const y2 = CY + R * Math.sin(a1);
    const largeArc = sweep > Math.PI ? 1 : 0;
    const d = `M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    wedges.push({
      d,
      fill: sliceFillColor(slice.color, slice.i),
      key: `${slice.label}-${slice.i}`,
      midAngle,
      frac,
      label: slice.label,
    });
    angle = a1 + gap;
  }

  const callouts = wedges
    .filter((w) => w.frac >= LABEL_MIN_FRAC)
    .map((w) => {
      const ang = w.midAngle;
      const cos = Math.cos(ang);
      const sin = Math.sin(ang);
      const x0 = CX + R * CALLOUT_INSET * cos;
      const y0 = CY + R * CALLOUT_INSET * sin;
      const x1 = CX + CALLOUT_LINE_END_R * cos;
      const y1 = CY + CALLOUT_LINE_END_R * sin;
      const tx = CX + CALLOUT_TEXT_R * cos;
      const ty = CY + CALLOUT_TEXT_R * sin;
      const name = truncateLabel(w.label, LABEL_MAX_CHARS);

      return (
        <g key={`callout-${w.key}`} className="pie-chart-callout">
          <line x1={x0} y1={y0} x2={x1} y2={y1} className="pie-chart-callout-line" />
          <text
            x={tx}
            y={ty}
            className="pie-chart-label pie-chart-wedge-name"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {name}
          </text>
        </g>
      );
    });

  return (
    <figure className="pie-chart-figure">
      <div className="pie-chart-wrap" role="img" aria-label={title}>
        <svg viewBox={VIEW_BOX} className="pie-chart-svg" aria-hidden overflow="visible">
          <title>{title}</title>
          {wedges.map((w) => (
            <path
              key={w.key}
              d={w.d}
              fill={w.fill}
              stroke="var(--border)"
              strokeWidth={1.25}
              strokeLinejoin="round"
            />
          ))}
          {callouts}
        </svg>
      </div>
      <figcaption className="pie-chart-caption muted small">{caption}</figcaption>
    </figure>
  );
}
